/** @module sagas/user */
import { put, call, select, fork, take } from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import * as Api from "../../api";
import * as actions from "../actions";
import * as types from "../actions/actionTypes";
import history from "../../history";
import { setSessionCookie } from "../../helpers/cookies";
import {
  hasSufficientDataToSendBranchEmail,
  getBranchEmail,
} from "../../helpers/helpers";
import { requestTimesheetData } from "./timesheet";
import { requestPayslipsData } from "./payslips";
import { requestHolidayData } from "./holiday";

const getData = (state) => state.core.app;
const getOnboarding = (state) => state.core.onboardState;
const getBranches = (state) => state.core.umbracoContent.branches;

const stringifyObjects = (obj) => {
  const newObj = { ...obj };
  Object.keys(obj).forEach((key) => {
    newObj[key] =
      typeof newObj[key] === "object" && !(newObj[key] instanceof File)
        ? JSON.stringify(newObj[key])
        : newObj[key];
  });
  return newObj;
};

const wait = async (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export function* getUserSaga(_, shouldShowLoading = true) {
  try {
    yield put(actions.userSetIsLoading(true));
    // 1. Set loading state to display a spinner
    yield put(actions.setLoadingState(shouldShowLoading));
    // 2. Get B2C id token
    // 3. Get candidate data from Profile
    const response = yield call(Api.getUser);
    // 4. Store user data in app
    if (response.status === 200) {
      const { data: candidateData } = response.data;
      if (typeof candidateData === "object") {
        yield put(actions.storeUserData(candidateData));
        if (
          candidateData.permissions &&
          candidateData.permissions.seeTimesheets
        ) {
          yield call(requestTimesheetData);
        }
        if (
          candidateData.permissions &&
          candidateData.permissions.seePayslips
        ) {
          yield call(requestPayslipsData);
        }
        if (
          candidateData.permissions &&
          candidateData.permissions.seePayslips
        ) {
          yield call(requestHolidayData);
        }
      } else if (typeof candidateData === "string") {
        yield put(actions.setError(candidateData));
      }
      // 4.1 Hide a spinner
      yield put(actions.setLoadingState(false));
    }
  } catch (err) {
    yield put(actions.setLoadingState(false));
    const { type } = _;
    if (type === types.UPDATE_CV_SILENT) {
      yield put(actions.addError("cv", err));
    } else {
      yield put(actions.addError("user", err));
    }
  } finally {
    yield put(actions.userSetIsLoading(false));
  }
}

const shouldSendBranchEmail = (oldUser, updatedUser) => {
  const hasBranchChanged =
    updatedUser.responsibleTeam !== oldUser.responsibleTeam;
  const doesOldNeedUpdating = !hasSufficientDataToSendBranchEmail(oldUser);
  const isUpdatedSufficient = hasSufficientDataToSendBranchEmail(updatedUser);
  const shouldOldChange = doesOldNeedUpdating || hasBranchChanged;
  return shouldOldChange && isUpdatedSufficient;
};

function createUploaderChannel(userData) {
  return eventChannel((emit) => {
    const onProgress = ({ total, loaded }) => {
      const percentage = Math.round((loaded * 100) / total);
      emit(percentage);
    };
    Api.updateUser(userData, onProgress)
      .catch((e) => emit(new Error(e)))
      .then(() => emit(END));
    const unsubscribe = () => {};
    return unsubscribe;
  });
}

function* watchOnProgress(chan) {
  try {
    while (true) {
      const data = yield take(chan);
      yield put(actions.setCvProgress(data));
    }
  } catch (e) {
    yield put(actions.addError("cv", e));
  }
}

export function* updateUserSilentSaga(action) {
  const oldData = yield select(getData);
  try {
    const { userData, onboarding } = action;
    if (onboarding) {
      const onboardState = yield select(getOnboarding);
      yield put(
        actions.onboardUpdateState({
          ...onboardState,
          [onboarding]: true,
        })
      );
    }
    if (typeof userData === "object") {
      if (userData.references) {
        yield call(Api.postRefereesDetails, userData.references);
      }
      // 2.1 Put candidate data to Profile
      yield put(actions.storeUserData(userData));
    } else if (typeof userData === "string") {
      yield put(actions.setError(userData));
    }

    const updatedData = yield select(getData);
    const uploadChannel = yield call(
      createUploaderChannel,
      stringifyObjects(userData)
    );
    yield fork(watchOnProgress, uploadChannel);
  } catch (err) {
    const { type } = action;
    if (type === types.UPDATE_CV_SILENT) {
      yield put(actions.addError("cv", err));
    }
    yield put(actions.storeUserData(oldData));
  }
}

export function* updateCvSaga(action) {
  yield put(actions.setCvUploadLoading(true));
  yield call(updateUserSilentSaga, action);
  yield call(getUserSaga, action, false);
  yield put(actions.setCvUploadLoading(false));
}

/**
 * Updates user in the api, grabs the user data and updates the client
 */
export function* updateUserSaga(action) {
  const { userData, route } = action;
  try {
    // 1. Set loading state to display a spinner
    yield put(actions.setLoadingState(true));
    // 2. Get B2C id token
    // 3. Put candidate data to Profile
    yield call(Api.updateUser, stringifyObjects(userData));
    const { data: newUserData, status } = yield call(Api.getUser);
    if (status === 200) {
      if (typeof newUserData === "object") {
        yield put(actions.storeUserData(newUserData));
      } else if (typeof newUserData === "string") {
        yield put(actions.setError(newUserData));
      }
      // 3.1 Hide a spinner
      yield put(actions.setLoadingState(false));
      // 3.2 Push to next route
      yield history.push(route);
    }
  } catch (err) {
    // We need to handle errors better later
    yield put(actions.setError(err));
    yield history.push(route);
  }
}

export function* storeCvStatusInSession(action) {
  const { payload: userData } = action;
  yield setSessionCookie(
    "candidateHasCv",
    userData.cv && userData.cv.fileRef ? "true" : "false"
  );
}

export function* updateGeneralConsent() {
  try {
    const response = yield call(Api.postGeneralConsent);
    if (response.status !== 200) {
      yield put(actions.addError("updateGeneralConsent", response));
      throw new Error(
        `Failed to process general consent update: ${response.status}`
      );
    }
  } catch (error) {
    yield put(actions.addError("updateGeneralConsent", error));
  }
}

export function* submitClientFormSilentSaga(action) {
  try {
    const { formContent } = action.payload;
    const response = yield call(Api.submitClientForm, formContent);
    const { status, candidateJobs } = response;
    if (status === 200) {
      yield put(actions.storeUserData({ candidateJobs }));
    }
  } catch (err) {
    yield put(actions.addError("submitClientForm", err));
  }
}
